import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { getSupabaseClient } from '../lib/supabase';
import type { PackageOffer, PackageOfferUnit } from './SeekerDetailModal/types';
import { useGuideTimezone } from '../hooks/useGuideTimezone';
import { formatUtcInTimezone, formatTimeInTimezone, getTimezoneAbbreviation } from '../utils/timezoneUtils';

const LINK_ENABLED_GUIDE_ID = '47e9e849-d537-4668-afa6-8ff6123dc520';

type AvailabilitySlot = {
  time: string; // Legacy HH:MM
  available: boolean;
  // Timezone-aware fields
  start_utc?: string;
  end_utc?: string;
  guide_timezone?: string;
};

type AvailabilityDay = {
  date: string;
  dayOfWeek?: string;
  timeSlots: AvailabilitySlot[];
};

interface ScheduleSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  guideId: string | null;
  seekerId: string | null;
  seekerName?: string | null;
  seekerEmail?: string | null;
  packageOffers: PackageOffer[];
  onScheduled: (sessionId: string) => void;
}

const AVAILABILITY_WINDOW_DAYS = 30;
const CONSULTATION_DURATION_MINUTES = 20;

const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (date: string): string => {
  try {
    const dateObj = new Date(`${date}T00:00:00`);
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return date;
  }
};

const formatTimeForDisplay = (slot: AvailabilitySlot, guideTimezone: string): string => {
  // Prefer UTC timestamp if available
  if (slot.start_utc && slot.guide_timezone) {
    const timeStr = formatTimeInTimezone(slot.start_utc, guideTimezone);
    const tzAbbrev = getTimezoneAbbreviation(slot.start_utc, guideTimezone);
    return tzAbbrev ? `${timeStr} ${tzAbbrev}` : timeStr;
  }

  // Fallback to legacy time string
  const [hours, minutes] = slot.time.split(':').map((value) => Number.parseInt(value, 10));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return slot.time;
  }
  const dateObj = new Date();
  dateObj.setHours(hours, minutes, 0, 0);
  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const sortAvailabilityDays = (days: AvailabilityDay[]): AvailabilityDay[] => {
  return [...days].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
};

const canFitSession = (
  timeSlots: AvailabilitySlot[],
  startTime: string,
  durationMinutes: number
): boolean => {
  // Find the index of the start time
  const startIndex = timeSlots.findIndex(slot => slot.time === startTime);
  if (startIndex === -1 || !timeSlots[startIndex].available) {
    return false;
  }

  // Calculate how many 15-minute slots we need (assuming slots are 15 minutes apart)
  const slotsNeeded = Math.ceil(durationMinutes / 15);

  // Check if we have enough consecutive available slots
  for (let i = 0; i < slotsNeeded; i++) {
    const slotIndex = startIndex + i;
    if (slotIndex >= timeSlots.length || !timeSlots[slotIndex].available) {
      return false;
    }
  }

  return true;
};

const ScheduleSessionModal: React.FC<ScheduleSessionModalProps> = ({
  isOpen,
  onClose,
  guideId,
  seekerId,
  seekerName,
  seekerEmail,
  packageOffers,
  onScheduled,
}) => {
  const { timezone: guideTimezone } = useGuideTimezone();
  const [sessionType, setSessionType] = useState<'consultation' | 'package'>('package');
  const [selectedUnit, setSelectedUnit] = useState<PackageOfferUnit | null>(null);
  const [availability, setAvailability] = useState<AvailabilityDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedStartUtc, setSelectedStartUtc] = useState<string>(''); // NEW: Track selected UTC time
  const [locationKind, setLocationKind] = useState<'online' | 'in_person' | 'link'>('online');
  const [locationDetails, setLocationDetails] = useState('');
  const [sessionLink, setSessionLink] = useState('');
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'type' | 'unit' | 'schedule'>('unit');
  const [displayedMonth, setDisplayedMonth] = useState<Date>(() => new Date());

  const acceptedOffers = useMemo(
    () => packageOffers.filter(offer => offer.status === 'accepted'),
    [packageOffers]
  );

  const availableUnits = useMemo(() => {
    return acceptedOffers.flatMap(offer =>
      offer.units
        .filter(unit => unit.status === 'unallocated' || unit.status === 'scheduled')
        .map(unit => ({ ...unit, offerName: offer.name }))
    );
  }, [acceptedOffers]);

  const hasAcceptedPackages = acceptedOffers.length > 0 && availableUnits.length > 0;

  const durationMinutes = useMemo(() => {
    if (sessionType === 'consultation') {
      return CONSULTATION_DURATION_MINUTES;
    }

    if (sessionType === 'package' && selectedUnit) {
      // Use the unit's duration if available
      if (selectedUnit.durationMinutes) {
        return selectedUnit.durationMinutes;
      }

      // Fallback to component type-based duration
      const componentType = selectedUnit.componentType?.toLowerCase() || '';
      if (componentType === 'ceremony' || componentType === 'journey') {
        return 240;
      }
      return 60;
    }

    return CONSULTATION_DURATION_MINUTES;
  }, [sessionType, selectedUnit]);

  const normalizedToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const startOfCalendar = useMemo(() => {
    const firstOfMonth = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), 1);
    const start = new Date(firstOfMonth);
    start.setDate(start.getDate() - start.getDay());
    return start;
  }, [displayedMonth]);

  const goToPrevMonth = () => {
    setDisplayedMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setDisplayedMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const canGoPrevMonth = useMemo(() => {
    const prevMonth = new Date(displayedMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const currentMonth = new Date(normalizedToday.getFullYear(), normalizedToday.getMonth(), 1);
    const prevMonthStart = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1);
    return prevMonthStart >= currentMonth;
  }, [displayedMonth, normalizedToday]);

  const calendarDays = useMemo(() => {
    const days: Array<{
      dateKey: string;
      label: number;
      isPast: boolean;
      hasSlots: boolean;
      isSelected: boolean;
      isCurrentMonth: boolean;
    }> = [];

    for (let i = 0; i < 42; i += 1) {
      const currentDate = new Date(startOfCalendar);
      currentDate.setDate(startOfCalendar.getDate() + i);
      const dateKey = formatDateKey(currentDate);
      const isPast = currentDate < normalizedToday && dateKey !== formatDateKey(normalizedToday);
      const isCurrentMonth = currentDate.getMonth() === displayedMonth.getMonth();
      const dayAvailability = availability.find((day) => day.date === dateKey);
      // Check if there are any slots that can fit the full session duration
      const hasSlots = dayAvailability?.timeSlots?.some((slot) =>
        canFitSession(dayAvailability.timeSlots, slot.time, durationMinutes)
      ) ?? false;
      const isSelected = selectedDate === dateKey;

      days.push({
        dateKey,
        label: currentDate.getDate(),
        isPast,
        hasSlots,
        isSelected,
        isCurrentMonth,
      });
    }

    return days;
  }, [availability, normalizedToday, selectedDate, startOfCalendar, durationMinutes, displayedMonth]);

  const selectedDaySlots = useMemo(() => {
    if (!selectedDate) return [];
    const daySlots = availability.find((day) => day.date === selectedDate)?.timeSlots ?? [];

    // Filter slots to only show those that can fit the full session duration
    return daySlots.map((slot) => ({
      ...slot,
      available: slot.available && canFitSession(daySlots, slot.time, durationMinutes),
    }));
  }, [availability, selectedDate, durationMinutes]);

  const selectedDayHasSlots = selectedDaySlots.length > 0;
  const availabilityHasOptions = availability.some((day) =>
    day.timeSlots.some((slot) => canFitSession(day.timeSlots, slot.time, durationMinutes))
  );
  const monthLabel = displayedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  useEffect(() => {
    if (!isOpen) {
      setSessionType('package');
      setSelectedUnit(null);
      setAvailability([]);
      setSelectedDate('');
      setSelectedTime('');
      setSelectedStartUtc('');
      setError(null);
      setLoadingAvailability(false);
      setIsSaving(false);
      setLocationKind('online');
      setLocationDetails('');
      setSessionLink('');
      setStep('type');
      setDisplayedMonth(new Date());
    }
  }, [isOpen]);

  useEffect(() => {
    let cancelled = false;

    const loadAvailability = async () => {
      if (!isOpen || !guideId || step !== 'schedule') {
        return;
      }

      setLoadingAvailability(true);
      setError(null);

      try {
        const env = (window as any).__ENV__;
        const baseUrl = env?.SUPABASE_URL;
        if (!baseUrl) {
          throw new Error('Missing Supabase URL for availability.');
        }

        const today = new Date();
        const startDate = today.toISOString().split('T')[0];
        const endDateObj = new Date(today.getTime() + AVAILABILITY_WINDOW_DAYS * 24 * 60 * 60 * 1000);
        const endDate = endDateObj.toISOString().split('T')[0];

        const response = await fetch(
          `${baseUrl}/functions/v1/guide-availability?guide_id=${guideId}&start_date=${startDate}&end_date=${endDate}`
        );

        if (!response.ok) {
          throw new Error('Unable to load guide availability.');
        }

        const data = await response.json();
        const availabilityDays: AvailabilityDay[] = sortAvailabilityDays(data.availability ?? []);

        if (cancelled) return;

        setAvailability(availabilityDays);

        const firstAvailableDay = availabilityDays.find((day) =>
          day.timeSlots.some((slot) => canFitSession(day.timeSlots, slot.time, durationMinutes)),
        );
        if (firstAvailableDay) {
          setSelectedDate(firstAvailableDay.date);
          const firstAvailableSlot = firstAvailableDay.timeSlots.find((slot) =>
            canFitSession(firstAvailableDay.timeSlots, slot.time, durationMinutes)
          );
          setSelectedTime(firstAvailableSlot ? firstAvailableSlot.time : '');
        } else {
          setSelectedDate('');
          setSelectedTime('');
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load availability.';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoadingAvailability(false);
        }
      }
    };

    void loadAvailability();

    return () => {
      cancelled = true;
    };
  }, [isOpen, guideId, step, durationMinutes]);

  const handleSelectSessionType = (type: 'consultation' | 'package') => {
    setSessionType(type);
    setError(null);

    if (type === 'consultation') {
      setStep('schedule');
    } else if (type === 'package') {
      if (hasAcceptedPackages) {
        setStep('unit');
      } else {
        setError('No accepted packages with available units to schedule.');
      }
    }
  };

  const handleSelectUnit = (unit: PackageOfferUnit & { offerName: string }) => {
    setSelectedUnit(unit);
    setError(null);
    setStep('schedule');
  };

  const handleSelectDate = (value: string) => {
    setSelectedDate(value);
    setSelectedTime('');
    setSelectedStartUtc(''); // Clear UTC time when date changes
    setError(null);
  };

  const handleSelectTime = (slot: AvailabilitySlot, available: boolean) => {
    if (!available) return;

    // Store the UTC time for comparison and submission
    if (slot.start_utc) {
      setSelectedStartUtc(slot.start_utc);
    } else {
      setSelectedStartUtc('');
    }

    // Store slot.time to match isSelected comparison (which compares against slot.time)
    setSelectedTime(slot.time);

    setError(null);
  };

  const handleSubmit = async () => {
    if (!guideId || !seekerId) {
      setError('Missing required information to schedule this session.');
      return;
    }

    if (!selectedDate || !selectedTime) {
      setError('Select a date and time before scheduling.');
      return;
    }

    if (locationKind === 'in_person' && !locationDetails.trim()) {
      setError('Provide location details for in-person sessions.');
      return;
    }

    if (locationKind === 'link' && !sessionLink.trim()) {
      setError('Provide a meeting link for link sessions.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();

      const body: any = {
        guide_id: guideId,
        seeker_id: seekerId,
        session_type: 'consultation',
        // Keep legacy fields for backward compatibility
        session_date: selectedDate,
        session_time: `${selectedTime}:00`,
        // NEW: Include UTC timestamp if available
        start_utc: selectedStartUtc || undefined,
        duration_minutes: durationMinutes,
        seeker_name: seekerName || null,
        seeker_email: seekerEmail || null,
        location: locationKind,
        location_details: locationKind === 'in_person' ? locationDetails.trim() : undefined,
        session_link: locationKind === 'link' ? sessionLink.trim() : undefined,
      };

      if (sessionType === 'package' && selectedUnit) {
        body.notes = `Package unit: ${selectedUnit.componentName}`;
        body.package_offer_unit_id = selectedUnit.id;

        // Map component type to session type
        const componentType = selectedUnit.componentType?.toLowerCase() || '';
        const sessionTypeMap: Record<string, string> = {
          preparation: 'preparation',
          integration: 'integration',
          ceremony: 'journey',
          journey: 'journey',
          additional: 'follow_up',
          follow_up: 'follow_up',
        };
        body.session_type = sessionTypeMap[componentType] || 'consultation';
      }

      const { data, error } = await supabase.functions.invoke('session-schedule', {
        body,
      });

      if (error) {
        throw new Error(error.message || 'Failed to schedule session');
      }

      console.log('Session scheduled:', data);
      onScheduled(data.session_id);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save session.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (step === 'schedule') {
      setStep('unit');
      setSelectedDate('');
      setSelectedTime('');
      setAvailability([]);
    } else if (step === 'unit') {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  const getTitle = () => {
    if (step === 'type') return 'Schedule Session';
    if (step === 'unit') return 'Select Package Unit';
    return sessionType === 'consultation' ? 'Schedule Consultation' : 'Schedule Package Session';
  };

  const canSubmit =
    !isSaving &&
    guideId &&
    seekerId &&
    selectedDate &&
    selectedTime &&
    (locationKind !== 'in_person' || locationDetails.trim().length > 0) &&
    (locationKind !== 'link' || sessionLink.trim().length > 0);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#2C2731]/80 px-3 py-6">
      <div className="w-full max-w-[560px]">
        <div className="flex max-h-[92vh] flex-col overflow-hidden rounded-[32px] border border-[#2C2731] bg-[#FFFCEF] shadow-[0_30px_120px_-40px_rgba(32,77,60,0.65)]">
          <header className="flex h-16 items-center border-b border-[#2C2731]/30 bg-[#2C2731] px-4">
            <button
              type="button"
              onClick={step === 'type' ? onClose : handleBack}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
              aria-label={step === 'type' ? 'Close' : 'Back'}
            >
              {'\u2190'}
            </button>
            <div className="flex flex-1 justify-center">
              <h3 className="text-lg font-semibold text-[#FFFCEF]">{getTitle()}</h3>
            </div>
            <div className="w-11" aria-hidden />
          </header>

          <main className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pb-6 pt-6">
            {error && (
              <div className="rounded-2xl border border-[#F87171]/60 bg-white/90 px-4 py-3 text-sm text-[#7A1E1E]">
                {error}
              </div>
            )}

            {(!guideId || !seekerId) && (
              <div className="rounded-2xl border border-[#F97316]/50 bg-white/85 px-4 py-3 text-sm text-[#7C4A03]">
                Invite this seeker to create an account before scheduling sessions.
              </div>
            )}

            {step === 'type' && (
              <div className="space-y-4">
                <p className="text-sm text-[#2C2731]/70">
                  Choose the type of session you'd like to schedule:
                </p>

                <button
                  type="button"
                  onClick={() => handleSelectSessionType('consultation')}
                  className="w-full rounded-2xl border border-[#2C2731]/20 bg-white p-6 text-left transition hover:border-[#2C2731] hover:bg-white/90"
                >
                  <h4 className="text-lg font-semibold text-[#2C2731]">Consultation</h4>
                  <p className="mt-2 text-sm text-[#2C2731]/70">
                    Schedule a 20-minute consultation session
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => handleSelectSessionType('package')}
                  disabled={!hasAcceptedPackages}
                  className={`w-full rounded-2xl border p-6 text-left transition ${
                    hasAcceptedPackages
                      ? 'border-[#2C2731]/20 bg-white hover:border-[#2C2731] hover:bg-white/90'
                      : 'cursor-not-allowed border-[#2C2731]/10 bg-[#F3EBCD] opacity-60'
                  }`}
                >
                  <h4 className="text-lg font-semibold text-[#2C2731]">Package Session</h4>
                  <p className="mt-2 text-sm text-[#2C2731]/70">
                    {hasAcceptedPackages
                      ? 'Schedule a session from an accepted package'
                      : 'No accepted packages available'}
                  </p>
                </button>
              </div>
            )}

            {step === 'unit' && (
              <div className="space-y-4">
                {availableUnits.length === 0 ? (
                  <div className="rounded-2xl border border-[#F97316]/50 bg-white/85 px-4 py-3 text-sm text-[#7C4A03]">
                    No accepted packages with available units to schedule. Send a package offer first.
                  </div>
                ) : (
                <p className="text-sm text-[#2C2731]/70">
                  Select which package unit to schedule:
                </p>
                )}

                {availableUnits.map((unit) => (
                  <button
                    key={unit.id}
                    type="button"
                    onClick={() => handleSelectUnit(unit)}
                    className={`w-full rounded-2xl border p-6 text-left transition ${
                      selectedUnit?.id === unit.id
                        ? 'border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]'
                        : 'border-[#2C2731]/20 bg-white hover:border-[#2C2731]'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className={`font-semibold ${selectedUnit?.id === unit.id ? 'text-[#FFFCEF]' : 'text-[#2C2731]'}`}>
                          {unit.componentName}
                        </h4>
                        <p className={`mt-1 text-sm ${selectedUnit?.id === unit.id ? 'text-[#FFFCEF]/80' : 'text-[#2C2731]/70'}`}>
                          {unit.offerName}
                        </p>
                      </div>
                      <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                        unit.status === 'scheduled'
                          ? selectedUnit?.id === unit.id
                            ? 'border-[#FFFCEF]/40 bg-[#FFFCEF]/20 text-[#FFFCEF]'
                            : 'border-[#2C2731]/40 bg-[#E8DFBD] text-[#2C2731]'
                          : selectedUnit?.id === unit.id
                            ? 'border-[#FFFCEF]/40 bg-[#FFFCEF]/20 text-[#FFFCEF]'
                            : 'border-[#2C2731]/30 bg-[#FFFCEF] text-[#2C2731]'
                      }`}>
                        {unit.status === 'scheduled' ? 'Reschedule' : 'Schedule'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {step === 'schedule' && (
              <>
                <section className="rounded-[32px] border border-[#2C2731]/20 bg-white/90 p-6 shadow-[0_32px_60px_-35px_rgba(32,77,60,0.45)]">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70">
                    Session Details
                  </p>
                  <h1 className="mt-2 text-xl font-semibold text-[#2C2731]">
                    {sessionType === 'consultation'
                      ? 'Consultation'
                      : selectedUnit?.componentName || 'Package Session'}
                  </h1>
                  <p className="mt-1 text-sm text-[#2C2731]/70">
                    {sessionType === 'consultation'
                      ? `${durationMinutes} minutes`
                      : `${selectedUnit?.componentType || 'Session'} · ${durationMinutes} minutes`}
                  </p>
                </section>

                <section className="rounded-3xl border border-[#2C2731]/20 bg-white/85 p-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70">Location</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    {[
                      { value: 'online' as const, label: 'Online' },
                      { value: 'in_person' as const, label: 'In person' },
                      ...(guideId === LINK_ENABLED_GUIDE_ID ? [{ value: 'link' as const, label: 'Link' }] : []),
                    ].map((option) => {
                      const isActive = locationKind === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setLocationKind(option.value)}
                          className={[
                            'rounded-2xl border px-4 py-3 text-sm font-semibold transition',
                            isActive
                              ? 'border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]'
                              : 'border-[#2C2731]/25 bg-[#FFFCEF] text-[#2C2731] hover:border-[#2C2731] hover:bg-[#2C2731] hover:text-[#FFFCEF]',
                          ].join(' ')}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  {locationKind === 'link' && (
                    <div className="mt-4">
                      <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/60">
                        Meeting link
                      </label>
                      <input
                        type="url"
                        value={sessionLink}
                        onChange={(event) => setSessionLink(event.target.value)}
                        placeholder="Paste Zoom, Google Meet, or other meeting link"
                        className="mt-2 w-full rounded-2xl border border-[#2C2731]/20 bg-white px-4 py-3 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none"
                      />
                    </div>
                  )}
                  {locationKind === 'in_person' && (
                    <div className="mt-4">
                      <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/60">
                        Location details
                      </label>
                      <textarea
                        value={locationDetails}
                        onChange={(event) => setLocationDetails(event.target.value)}
                        rows={3}
                        placeholder="Add address, entrance instructions, or parking notes"
                        className="mt-2 w-full rounded-2xl border border-[#2C2731]/20 bg-white px-4 py-3 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none"
                      />
                      <p className="mt-2 text-xs text-[#2C2731]/60">
                        Only you can see these details. Share with the seeker once confirmed.
                      </p>
                    </div>
                  )}
                </section>

                {loadingAvailability ? (
                  <div className="rounded-[28px] border border-[#2C2731]/15 bg-white/80 p-6">
                    <div className="h-4 w-2/3 animate-pulse rounded-full bg-[#E8DFBD]" />
                    <div className="mt-4 h-40 animate-pulse rounded-[24px] bg-[#E8DFBD]" />
                  </div>
                ) : availabilityHasOptions ? (
                  <>
                    <div className="rounded-3xl border border-[#2C2731]/15 bg-white/85 p-6">
                      <div className="flex items-center justify-between text-[#2C2731]">
                        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70">
                          Select a date
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={goToPrevMonth}
                            disabled={!canGoPrevMonth}
                            className="rounded-full p-1 text-[#2C2731]/70 transition hover:bg-[#2C2731]/10 disabled:opacity-30 disabled:cursor-not-allowed"
                            aria-label="Previous month"
                          >
                            <ChevronLeftIcon className="h-5 w-5" />
                          </button>
                          <span className="min-w-[140px] text-center text-sm font-medium text-[#2C2731]/70">{monthLabel}</span>
                          <button
                            type="button"
                            onClick={goToNextMonth}
                            className="rounded-full p-1 text-[#2C2731]/70 transition hover:bg-[#2C2731]/10"
                            aria-label="Next month"
                          >
                            <ChevronRightIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      <p className="mt-1 text-xs text-[#2C2731]/60">All times shown in {guideTimezone}</p>
                      <div className="mt-4 grid grid-cols-7 gap-2 text-center text-xs font-semibold text-[#2C2731]/70">
                        {DAY_LABELS.map((label) => (
                          <div key={label}>{label}</div>
                        ))}
                      </div>
                      <div className="mt-2 grid grid-cols-7 gap-2">
                        {calendarDays.map((day) => (
                          <button
                            key={day.dateKey}
                            type="button"
                            onClick={() => (!day.isPast && day.hasSlots ? handleSelectDate(day.dateKey) : null)}
                            disabled={day.isPast || !day.hasSlots}
                            className={[
                              'h-12 rounded-2xl border text-sm font-semibold transition disabled:cursor-not-allowed',
                              day.isSelected
                                ? 'border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]'
                                : day.hasSlots && day.isCurrentMonth && !day.isPast
                                  ? 'border-[#2C2731]/20 bg-[#FFFCEF] text-[#2C2731] hover:border-[#2C2731]'
                                  : 'border-transparent bg-transparent text-[#C7C1AD]',
                            ].join(' ')}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {selectedDate && (
                      <div className="rounded-3xl border border-[#2C2731]/15 bg-[#FFFCEF] p-6">
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70">
                          Available times
                        </p>
                        {selectedDayHasSlots ? (
                          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                            {selectedDaySlots.map((slot) => {
                              // Check if this slot is selected by comparing UTC time or legacy time
                              const isSelected = slot.available && (
                                (slot.start_utc && selectedStartUtc && slot.start_utc === selectedStartUtc) ||
                                (!slot.start_utc && selectedTime === slot.time)
                              );
                              return (
                                <button
                                  key={slot.start_utc || slot.time}
                                  type="button"
                                  onClick={() => handleSelectTime(slot, slot.available)}
                                  disabled={!slot.available}
                                  className={[
                                    'rounded-full border px-4 py-3 text-sm font-semibold transition',
                                    isSelected
                                      ? 'border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]'
                                      : slot.available
                                        ? 'border-[#2C2731]/20 bg-white text-[#2C2731] hover:border-[#2C2731]'
                                        : 'cursor-not-allowed border-[#C7C1AD]/40 bg-[#F4EAD1] text-[#C7C1AD] line-through',
                                  ].join(' ')}
                                >
                                  {formatTimeForDisplay(slot, guideTimezone)}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="mt-4 text-sm text-[#2C2731]/70">
                            No available times for this date.
                          </p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="rounded-3xl border border-[#2C2731]/20 bg-white/80 px-5 py-4 text-sm text-[#2C2731]/70">
                    There are no open windows in the next {AVAILABILITY_WINDOW_DAYS} days. Update your calendar blocks to
                    continue.
                  </p>
                )}
              </>
            )}
          </main>

          <footer className="flex items-center gap-3 border-t border-[#2C2731]/20 bg-gradient-to-t from-[#FFFCEF] via-[#FFFCEF] to-[#FFFCEF]/0 px-6 pb-6 pt-4">
            {step === 'schedule' && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="group inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#2C2731] bg-[#2C2731] px-6 py-4 text-lg font-semibold text-[#FFFCEF] transition hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-50"
              >
                <span className="invisible w-5 text-xl leading-none">{'\u2190'}</span>
                <span>{isSaving ? 'Saving…' : 'Schedule Session'}</span>
                <span className="w-5 text-xl leading-none">{'\u2192'}</span>
              </button>
            )}
            {step === 'unit' && selectedUnit && (
              <button
                type="button"
                onClick={() => setStep('schedule')}
                className="group inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#2C2731] bg-[#2C2731] px-6 py-4 text-lg font-semibold text-[#FFFCEF] transition hover:-translate-y-0.5"
              >
                <span className="invisible w-5 text-xl leading-none">{'\u2190'}</span>
                <span>Continue</span>
                <span className="w-5 text-xl leading-none">{'\u2192'}</span>
              </button>
            )}
          </footer>
        </div>
      </div>
    </div>
  );
};

export default ScheduleSessionModal;
